// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BaseInput, BaseTable } from '~/components/common';
// Others
import { IHeaderCellType } from '~/utils/interface/common';
import { EMPTY_STRING } from '~/utils/constants/common';
import { generateUniqueId } from '~/utils/helper/common';
import { TypeErrorFormEnum } from '~/utils/enum/common';
import { IChecklist, IFormInventory } from '~/utils/interface/inventory';
// Styles, images, icons
import styles from './ChecklistInventoryForm.module.scss';
import { icons } from '~/assets';

type Props = {
  onRemoveChecklist?: (id: string) => void;
};

const cx = classNames.bind(styles);

const ChecklistInventoryForm = (props: Props) => {
  //#region Destructuring Props
  const { onRemoveChecklist } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/inventory', 'common/base']);

  const {
    control,
    watch,
    setError,
    formState: { errors },
  } = useFormContext<IFormInventory>();

  const { append: appendItems, remove: removeItems } = useFieldArray({
    control,
    name: 'checklistItems',
  });

  const { checklistItems: listChecklists } = watch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleAddItem = () => {
    const newItem: IChecklist = {
      id: generateUniqueId(),
      name: EMPTY_STRING,
      description: EMPTY_STRING,
    };

    appendItems(newItem);
    setError('checklistItems', { type: TypeErrorFormEnum.MANUAL, message: EMPTY_STRING });
  };

  const columnsItem = (t: TFunction): IHeaderCellType<IChecklist>[] => {
    return [
      {
        id: 'name',
        label: t('inventory_form_title_name_checklist'),
        render: (record: IChecklist, index: number) => {
          return (
            <Controller
              key={`${record.id}-${index}-name`}
              name={`checklistItems.${index}.name`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseInput height={32} borderRadius={'4px'} value={value} onChange={onChange} />
              )}
            />
          );
        },
        minWidth: 100,
      },
      {
        id: 'description',
        label: t('inventory_form_title_description_checklist'),
        render: (record: IChecklist, index: number) => {
          return (
            <Controller
              key={`${record.id}-${index}-description`}
              name={`checklistItems.${index}.description`}
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseInput height={32} borderRadius={'4px'} value={value} onChange={onChange} />
              )}
            />
          );
        },
        minWidth: 100,
      },
      {
        id: 'action',
        label: t('inventory_form_title_product_variants_action'),
        minWidth: 100,
        align: 'center',
        render: (record: IChecklist, index: number) => {
          return (
            <Stack direction={'row'} gap={1}>
              <IconButton
                className={cx('buttonIcon')}
                onClick={() => {
                  removeItems(index);
                  onRemoveChecklist?.(String(record?.id));
                }}
              >
                <img
                  className={cx('iconDelete')}
                  src={icons.commonIconTrash}
                  alt={t('common_alt_img', { ns: 'common/base' })}
                />
              </IconButton>
            </Stack>
          );
        },
      },
    ];
  };
  //#endregion Handle Function

  return (
    <Stack id='productChecklistInventoryFormComponent' gap={4}>
      <Typography className={cx('header')}>{t('inventory_form_title_product_checklist')}</Typography>

      <BaseTable columns={columnsItem(t)} dataSource={listChecklists} />

      <Box>
        <Button
          type='button'
          variant='outlined'
          onClick={handleAddItem}
          startIcon={<img src={icons.commonIconPlusPrimary} alt={'common_alt_img'} />}
        >
          {t('inventory_form_add_on_title_add_item')}
        </Button>
      </Box>
    </Stack>
  );
};

export default ChecklistInventoryForm;
